import { createContext, useContext, useEffect, useState } from "react";

const LangContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    const stored = localStorage.getItem("azroil-lang");
    if (stored === "en" || stored === "az") return stored;
    // First visit: auto-detect from browser language
    const langs = navigator.languages || [navigator.language || ""];
    const preferAz = langs.some((l) => typeof l === "string" && l.toLowerCase().startsWith("az"));
    return preferAz ? "az" : "en";
  });

  useEffect(() => {
    localStorage.setItem("azroil-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (obj) => {
    if (obj == null) return "";
    if (typeof obj === "string") return obj;
    return obj[lang] ?? obj.en ?? "";
  };

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used inside LanguageProvider");
  return ctx;
};

// UI string dictionary
export const ui = {
  brand: { en: "Az.r.Oil", az: "Az.r.Oil" },
  tagline: { en: "Industrial Chemistry", az: "Sənaye kimyası" },
  nav: {
    home: { en: "Home", az: "Ana səhifə" },
    about: { en: "About", az: "Haqqımızda" },
    products: { en: "Products", az: "Məhsullar" },
    contact: { en: "Contact", az: "Əlaqə" },
  },
  common: {
    view: { en: "View", az: "Bax" },
    viewProducts: { en: "View products", az: "Məhsullara bax" },
    allProducts: { en: "All products", az: "Bütün məhsullar" },
    learnMore: { en: "Learn more about us", az: "Haqqımızda daha çox" },
    viewCatalog: { en: "View full catalog", az: "Tam kataloqa bax" },
    aboutBrand: { en: "About Az.r.Oil", az: "Az.r.Oil haqqında" },
    browseProducts: { en: "Browse products", az: "Məhsullara keç" },
    backToCatalog: { en: "Back to catalogue", az: "Kataloqa qayıt" },
    results: { en: "results", az: "nəticə" },
    listedIn: { en: "Listed in", az: "Kateqoriyalar" },
    relatedProducts: { en: "Related products", az: "Əlaqəli məhsullar" },
    productNotFound: { en: "Product not found", az: "Məhsul tapılmadı" },
    productNotFoundDesc: {
      en: "The product you are looking for does not exist in our catalogue.",
      az: "Axtardığınız məhsul kataloqumuzda mövcud deyil.",
    },
  },
  home: {
    eyebrow: { en: "001 — Chemistry for Industry", az: "001 — Sənaye üçün kimya" },
    h1l1: {
      en: "A reliable supplier of industrial chemicals, oilfield tools and safety equipment for industrial, oil & gas and mining operations.",
      az: "Sənaye, neft-qaz və mədən əməliyyatları üçün sənaye kimyəvi maddələri, neft-qaz alətləri və mühafizə vasitələrinin etibarlı təchizatçısı.",
    },
    h1l2: { en: "", az: "" },
    heroLead: {
      en: "Az.r.Oil supplies, stores and delivers the chemicals, tools and protective equipment that industrial, oil & gas and mining operators rely on every day — backed by stable supply, verified quality and dependable logistics.",
      az: "Az.r.Oil sənaye, neft-qaz və mədən operatorlarının gündəlik fəaliyyətində istifadə etdiyi kimyəvi maddələrin, alətlərin və qoruyucu vasitələrin təchizatını, saxlanmasını və çatdırılmasını həyata keçirir — sabit təchizat, təsdiqlənmiş keyfiyyət və etibarlı logistika ilə.",
    },
    statLines: { en: "Product Lines", az: "Məhsul xətləri" },
    statGroups: { en: "Category Groups", az: "Kateqoriya qrupları" },
    statIndustries: { en: "Serviced Industries", az: "Xidmət edilən sahələr" },
    statIndustriesValue: { en: "Oil · Water · Food · Industrial", az: "Neft · Su · Qida · Sənaye" },
    statOrigin: { en: "Origin", az: "Mənşə" },
    statOriginValue: { en: "Baku, Azerbaijan", az: "Bakı, Azərbaycan" },
    aboutEyebrow: { en: "02 — About", az: "02 — Haqqımızda" },
    aboutTitle: {
      en: "A focused chemical supplier built for industrial reliability.",
      az: "Sənaye etibarlılığı üçün qurulmuş ixtisaslaşmış kimya təchizatçısı.",
    },
    aboutP1: {
      en: "We operate the full chemical supply chain — from sourcing and logistics to warehousing and delivery — so that production teams receive consistent, documented material on schedule. Every shipment is traceable, conforms to agreed specifications and is packaged for safe industrial handling.",
      az: "Biz tam kimyəvi təchizat zəncirini idarə edirik — mənbədən və logistikadan anbar və çatdırılmaya qədər — nəticədə istehsal komandaları sənədləşdirilmiş materialı vaxtında alır. Hər göndəriş izlənə bilir, razılaşdırılmış spesifikasiyalara cavab verir və təhlükəsiz sənaye istifadəsi üçün qablaşdırılır.",
    },
    aboutP2: {
      en: "Our technical catalogue covers oil & gas chemistry, water treatment, food and feed ingredients, and general industrial solvents.",
      az: "Texniki kataloqumuz neft və qaz kimyasını, su təmizləmə, qida və yem komponentlərini, həmçinin ümumi sənaye həlledicilərini əhatə edir.",
    },
    catEyebrow: { en: "Catalogue", az: "Kataloq" },
    catTitle: { en: "Product categories", az: "Məhsul kateqoriyaları" },
    catDesc: {
      en: "Four focused streams that cover the core chemistries required by industrial operators and food producers.",
      az: "Sənaye operatorlarının və qida istehsalçılarının ehtiyac duyduğu əsas kimyaları əhatə edən dörd istiqamət.",
    },
    featuredEyebrow: { en: "Featured", az: "Seçilmiş" },
    featuredTitle: { en: "Representative products", az: "Nümayəndə məhsullar" },
    networkEyebrow: { en: "Network", az: "Şəbəkə" },
    networkTitle: { en: "Manufacturers & partners", az: "İstehsalçılar və tərəfdaşlar" },
    networkDesc: {
      en: "We work with a network of established global producers to source chemistries that match agreed specifications and documentation standards.",
      az: "Razılaşdırılmış spesifikasiyalara və sənədləşdirmə standartlarına uyğun kimyəvi məhsulları əldə etmək üçün dünyanın tanınmış istehsalçılarından ibarət şəbəkə ilə işləyirik.",
    },
  },
  about: {
    crumb: { en: "About Az.r.Oil · 001", az: "Az.r.Oil haqqında · 001" },
    h1: {
      en: "A reliable supplier of industrial chemicals, oilfield tools and safety equipment for industrial, oil & gas and mining operations.",
      az: "Sənaye, neft-qaz və mədən əməliyyatları üçün sənaye kimyəvi maddələri, neft-qaz alətləri və mühafizə vasitələrinin etibarlı təchizatçısı.",
    },
    lead: {
      en: "We supply, store and deliver industrial chemicals, oilfield tools and safety equipment used in daily operations across industrial, oil & gas and mining sectors. Our focus is on consistent supply, verified quality and reliable logistics that support uninterrupted production.",
      az: "Sənaye, neft-qaz və mədən sahələrində gündəlik əməliyyatlarda istifadə olunan kimyəvi maddələrin, alətlərin və mühafizə vasitələrinin təchizatını, saxlanmasını və çatdırılmasını həyata keçiririk. Fəaliyyətimiz sabit təchizat, təsdiqlənmiş keyfiyyət və etibarlı logistika üzərində qurulub.",
    },
    missionEyebrow: { en: "Mission", az: "Missiya" },
    missionTitle: { en: "What we set out to do", az: "Məqsədimiz" },
    missionP1: {
      en: "Our mission is to give industrial customers a reliable source of documented, compliant chemistry. That means accurate specifications, consistent supply, clear documentation and responsive technical support — delivered without the friction that typically comes with imported chemical logistics.",
      az: "Missiyamız sənaye müştərilərinə sənədləşdirilmiş və standartlara uyğun kimyəvi məhsulların etibarlı mənbəyini təqdim etməkdir. Bu o deməkdir ki, dəqiq spesifikasiyalar, davamlı təchizat, aydın sənədləşmə və operativ texniki dəstək — kimyəvi maddələrin idxalı zamanı adətən qarşılaşılan çətinliklər olmadan çatdırılır.",
    },
    missionP2: {
      en: "We focus on long-term supply relationships rather than one-off transactions. This enables production teams to plan around our delivery and keep their operations predictable.",
      az: "Bizim diqqətimiz birdəfəlik əməliyyatlardan çox uzunmüddətli təchizat münasibətlərinə yönəlmişdir. Bu, istehsal komandalarına çatdırılmamızı planlaşdırmağa və əməliyyatlarını proqnozlaşdırıla bilən saxlamağa imkan verir.",
    },
    experienceEyebrow: { en: "Experience", az: "Təcrübə" },
    experienceTitle: {
      en: "Proven industry experience",
      az: "Sübut olunmuş sənaye təcrübəsi",
    },
    experienceP1: {
      en: "The company has been operating since 2004, building long-term partnerships and delivering consistent supply solutions across industrial sectors.",
      az: "Şirkət 2004-cü ildən fəaliyyət göstərir, sənaye sektorlarında uzunmüddətli tərəfdaşlıqlar quraraq davamlı təchizat həlləri təqdim edir.",
    },
    experienceP2: {
      en: "Over the years, we have cooperated with major regional and international companies, including SOCAR, LUKOIL, Azpetrol, Azfen, and Baku Hotel Company. This experience reflects our ability to meet strict operational, technical, and logistical requirements in demanding environments.",
      az: "İllər ərzində SOCAR, LUKOIL, Azpetrol, Azfen və Baku Hotel Company də daxil olmaqla əsas regional və beynəlxalq şirkətlərlə əməkdaşlıq etmişik. Bu təcrübə tələbkar mühitlərdə ciddi əməliyyat, texniki və logistik tələbləri yerinə yetirmək qabiliyyətimizi əks etdirir.",
    },
    valuesEyebrow: { en: "Principles", az: "Prinsiplər" },
    valuesTitle: { en: "Operating values", az: "İş prinsipləri" },
    processEyebrow: { en: "How we work", az: "İş prosesimiz" },
    processTitle: { en: "From enquiry to delivery", az: "Sorğudan çatdırılmaya qədər" },
    processDesc: {
      en: "A straightforward workflow that keeps responsibility, documentation and delivery clear at every step.",
      az: "Hər mərhələdə məsuliyyəti, sənədləşməni və çatdırılmanı aydın saxlayan sadə iş axını.",
    },
    ctaEyebrow: { en: "06 — Catalogue", az: "06 — Kataloq" },
    ctaTitle: { en: "Review our technical catalogue.", az: "Texniki kataloqumuzu nəzərdən keçirin." },
  },
  values: [
    {
      idx: "V1",
      title: { en: "Technical quality", az: "Texniki keyfiyyət" },
      body: {
        en: "Every shipment is matched to specification and supported by traceable documentation and certificates of analysis.",
        az: "Hər göndəriş spesifikasiyaya uyğunlaşdırılır və izlənilə bilən sənədlər və analiz sertifikatları ilə dəstəklənir.",
      },
    },
    {
      idx: "V2",
      title: { en: "Supply continuity", az: "Təchizatın davamlılığı" },
      body: {
        en: "Long-term agreements with established producers let us maintain stable pricing and dependable delivery windows.",
        az: "Tanınmış istehsalçılarla uzunmüddətli müqavilələr sabit qiymətlər və etibarlı çatdırılma müddətləri saxlamağa imkan verir.",
      },
    },
    {
      idx: "V3",
      title: { en: "Safe logistics", az: "Təhlükəsiz logistika" },
      body: {
        en: "Products are packaged for industrial handling and shipped through verified routes with full compliance paperwork.",
        az: "Məhsullar sənaye istifadəsi üçün qablaşdırılır və tam uyğunluq sənədləri ilə yoxlanılmış marşrutlarla göndərilir.",
      },
    },
    {
      idx: "V4",
      title: { en: "Responsible sourcing", az: "Məsuliyyətli mənbə seçimi" },
      body: {
        en: "We prioritise producers that meet modern environmental, quality and operational safety standards.",
        az: "Müasir ekoloji, keyfiyyət və əməliyyat təhlükəsizliyi standartlarına cavab verən istehsalçılara üstünlük veririk.",
      },
    },
  ],
  process: [
    {
      idx: "01",
      title: { en: "Requirement analysis", az: "Tələblərin təhlili" },
      body: {
        en: "We review your production requirement — volume, specification tolerance, delivery schedule — before issuing a formal proposal.",
        az: "Rəsmi təklif təqdim etməzdən əvvəl istehsal tələblərinizi — həcm, spesifikasiya dözümlülüyü, çatdırılma qrafiki — nəzərdən keçiririk.",
      },
    },
    {
      idx: "02",
      title: { en: "Matched supply", az: "Uyğun təchizat" },
      body: {
        en: "We select the right grade and manufacturer to fit your formulation and regulatory context, not a generic off-the-shelf option.",
        az: "Ümumi standart variant deyil, formulanıza və tənzimləmə kontekstinizə uyğun gələn dərəcəni və istehsalçını seçirik.",
      },
    },
    {
      idx: "03",
      title: { en: "Scheduled delivery", az: "Planlaşdırılmış çatdırılma" },
      body: {
        en: "Material is released on the agreed schedule with documented QC and transport under proper handling conditions.",
        az: "Material razılaşdırılmış cədvəl üzrə, sənədləşdirilmiş keyfiyyət nəzarəti və müvafiq daşıma şərtləri ilə təqdim olunur.",
      },
    },
  ],
  products: {
    crumb: { en: "Products · Catalogue", az: "Məhsullar · Kataloq" },
    h1: { en: "Technical catalogue", az: "Texniki kataloq" },
    lead: {
      en: "Documented grades across oil & gas, water treatment, food and industrial applications. Select a category to filter the list.",
      az: "Neft və qaz, su təmizləmə, qida və sənaye tətbiqləri üzrə sənədləşdirilmiş dərəcələr. Siyahını süzgəcdən keçirmək üçün kateqoriya seçin.",
    },
    filterAll: { en: "All products", az: "Bütün məhsullar" },
    category: { en: "Category", az: "Kateqoriya" },
  },
  detail: {
    home: { en: "Home", az: "Ana səhifə" },
    productsCrumb: { en: "Products", az: "Məhsullar" },
    productLabel: { en: "Product", az: "Məhsul" },
    descEyebrow: { en: "01 — Description", az: "01 — Təsvir" },
    overview: { en: "Overview", az: "Ümumi məlumat" },
    specEyebrow: { en: "02 — Specification", az: "02 — Spesifikasiya" },
    techData: { en: "Technical data", az: "Texniki məlumatlar" },
    pkgEyebrow: { en: "03 — Packaging", az: "03 — Qablaşdırma" },
    pkgTitle: { en: "Packaging & handling", az: "Qablaşdırma və daşıma" },
    relatedEyebrow: { en: "04 — Related", az: "04 — Əlaqəli" },
    shelfLife: { en: "Shelf life", az: "Saxlama müddəti" },
    storage: { en: "Storage", az: "Saxlama şərtləri" },
  },
  contact: {
    crumb: { en: "Contact · 001", az: "Əlaqə · 001" },
    h1: { en: "Speak directly to our team.", az: "Komandamızla birbaşa əlaqə saxlayın." },
    lead: {
      en: "For specifications, volumes or documentation requests, reach us by email or phone. An Az.r.Oil representative will respond within one business day.",
      az: "Spesifikasiya, həcm və ya sənəd sorğuları üçün e-poçt və ya telefonla əlaqə saxlayın. Az.r.Oil nümayəndəsi bir iş günü ərzində cavab verəcək.",
    },
    items: {
      email: { en: "Email", az: "E-poçt" },
      phone: { en: "Phone", az: "Telefon" },
      office: { en: "Office", az: "Ofis" },
      hours: { en: "Hours", az: "İş saatları" },
      officeValue: { en: "Baku, Azerbaijan", az: "Bakı, Azərbaycan" },
      hoursValue: { en: "Mon – Fri · 09:00 – 18:00 (GMT+4)", az: "B.e – C · 09:00 – 18:00 (GMT+4)" },
    },
    companyEyebrow: { en: "05 — Company", az: "05 — Şirkət" },
    companyTitle: { en: "Company information", az: "Şirkət məlumatları" },
    fields: {
      name: { en: "Name", az: "Ad" },
      activity: { en: "Activity", az: "Fəaliyyət" },
      activityValue: {
        en: "Wholesale supply of industrial and food chemicals",
        az: "Sənaye və qida kimyəvi maddələrinin topdan təchizatı",
      },
      segment: { en: "Segment", az: "Seqment" },
      segmentValue: {
        en: "B2B — Manufacturing, processing, water treatment, food production",
        az: "B2B — İstehsal, emal, su təmizləmə, qida istehsalı",
      },
      location: { en: "Location", az: "Yerləşmə" },
      locationValue: { en: "Baku, Azerbaijan", az: "Bakı, Azərbaycan" },
    },
  },
  footer: {
    desc: {
      en: "A specialist supplier of industrial and food chemicals to manufacturers, plants and processors across the region.",
      az: "Regiondakı istehsalçılara, müəssisələrə və emalçılara sənaye və qida kimyəvi maddələri təchiz edən ixtisaslaşmış təchizatçı.",
    },
    nav: { en: "Navigation", az: "Naviqasiya" },
    cats: { en: "Categories", az: "Kateqoriyalar" },
    contact: { en: "Contact", az: "Əlaqə" },
    rights: { en: "All Rights Reserved", az: "Bütün hüquqlar qorunur" },
    tag: { en: "B2B · Industrial Chemistry · Est. Baku", az: "B2B · Sənaye Kimyası · Bakı" },
  },
  specKeys: {
    "CAS No.": { en: "CAS No.", az: "CAS №" },
    "HS Code": { en: "HS Code", az: "HS Kodu" },
    "Chemical Formula": { en: "Chemical Formula", az: "Kimyəvi düstur" },
    "Forms Supplied": { en: "Forms Supplied", az: "Təchiz olunan formalar" },
    "Purity": { en: "Purity", az: "Saflıq" },
    "Appearance": { en: "Appearance", az: "Görünüş" },
    "Solubility": { en: "Solubility", az: "Həll olma" },
    "Concentration": { en: "Concentration", az: "Qatılıq" },
    "Specific Gravity": { en: "Specific Gravity", az: "Xüsusi çəki" },
    "Type": { en: "Type", az: "Növ" },
    "Material": { en: "Material", az: "Material" },
    "Application": { en: "Application", az: "Tətbiq sahəsi" },
  },
  groupLabels: {
    chemicals: { en: "Chemicals", az: "Kimyəvi Maddələr" },
    "tools-ppe": { en: "Industrial Tools & PPE", az: "Sənaye Alətləri və FQV" },
  },
  productLabel: {
    chemicals: { en: "Product · Chemicals", az: "Məhsul · Kimyəvi maddələr" },
    "tools-ppe": { en: "Product · Tools & PPE", az: "Məhsul · Alətlər və FQV" },
  },
};
